import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import { Trash } from '@phosphor-icons/react';
import type { DeleteDeploymentResponse } from 'api/deployment/handlers/deploymentDeleteHandler';
import { DeploymentDeleteRequest } from 'common/deployment/DeploymentApiRoutesDocuments';
import Api from 'client/lib/Api';
import { useError } from 'client/lib/Error';
import { useAccount, useUser } from 'client/lib/User';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { Button } from 'client/components/Button';
import { useConfirmDelete } from 'client/components/Dialog/DeleteDialog';

export const SettingsActions: FC = () => {
  const account = useAccount();
  const { updateSession } = useUser();
  const navigate = useNavigate();
  const { setError } = useError();
  const confirmDelete = useConfirmDelete();

  const deploymentID = useDeploymentClientState((state) => state.document?.id || null);
  const projectID = useDeploymentClientState((state) => state.project?.id || null);
  const name = useDeploymentClientState((state) => state.document?.name || '');

  const handleDelete = useCallback(async () => {
    if (!deploymentID) return;

    const isOk = await confirmDelete({ targetName: name || '', targetType: 'deployment' });
    if (!isOk) return;

    const response = await Api.post<DeploymentDeleteRequest, DeleteDeploymentResponse>('/deployment/delete', {
      deploymentID,
    });
    if (response.status !== 'ok') {
      setError('Unable to delete deployment. Please try again…', { errorKey: 'delete-deployment', status: response.status });
      return;
    }

    updateSession();
    navigate(`/${account?.identifier}/project/${projectID}`);
  }, [deploymentID, name, confirmDelete, navigate, updateSession, account?.identifier, projectID]);

  return (
    <div css={containerStyle}>
      <Button danger icon={<Trash />} onClick={handleDelete}>
        Delete Deployment
      </Button>
    </div>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
`;
